import { createAsyncThunk } from "@reduxjs/toolkit";

import * as basketAPI from "../../../api/basket";
import { reloadBasket } from "../../../store/basket/thunks";

/**
 * Arguments for removeVoucherCode thunk.
 */
interface RemoveVoucherCodeArgs {
    code: string;
}

/**
 * Remove a voucher code from the basket.
 *
 * This thunk removes a previously applied voucher code from the user's
 * basket and reloads the basket to reflect updated pricing. Used in
 * checkout flows when users want to remove or replace promo codes.
 *
 * @param {string} args.code - The voucher code to remove
 * @returns {Promise<void>} Promise resolving when voucher is removed and basket reloaded
 *
 * @example
 * // Remove a promo code
 * dispatch(removeVoucherCode({ code: 'SUMMER2024' }));
 *
 * @sideEffects
 * - Makes API call to remove voucher via basketAPI.removeVoucherCode
 * - Dispatches reloadBasket thunk to reload basket data without the discount
 * - Updates `data.basket` with reloaded basket via extraReducers
 * - May trigger offer and pricing recalculations on the backend
 */
export const removeVoucherCode = createAsyncThunk(
    "checkout/removeVoucherCode",
    async (args: RemoveVoucherCodeArgs, { dispatch }): Promise<void> => {
        await basketAPI.removeVoucherCode(args.code);
        await dispatch(reloadBasket()).unwrap();
    },
);
